import { AuxNotificationStyleEnum, AuxNotificationToastTypeEnum } from "@blk/aladdin-web-components";
import { createSelector } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { useSelector } from "react-redux";
import { setQuarterSelected, setSelectedCampaign } from "./campaigns";
import { moduleName } from "./common";
import { getEndOfQuarter } from "common/date-utils";
import { language } from "language/default";
import { getConfig } from "plugins/config-loader";
import { addNotification } from "store/notification";
let tokenGetter = null;
export const defineTokenGetter = (getter) => {
    tokenGetter = (state) => getter(state);
};
const campaignErrorNotification = (message = language.en.campaigns.errorMsg) => addNotification({
    id: Date.now().toString(),
    message,
    notificationStyle: AuxNotificationStyleEnum.ERROR,
    showMessageCount: false,
    toastType: AuxNotificationToastTypeEnum.PERSISTENT
});
const api = createApi({
    baseQuery: fetchBaseQuery({
        baseUrl: `${getConfig("REACT_APP_CAMPAIGNS_BASE_URL")}campaigns`,
        prepareHeaders: (headers, { getState }) => {
            if (tokenGetter !== null) {
                const token = tokenGetter(getState());
                if (token) {
                    headers.set("authorization", `Bearer ${token}`);
                }
            }
            return headers;
        }
    }),
    endpoints: (builder) => ({
        getCampaignSubmissionTemplate: builder.query({
            providesTags: ["Campaigns"],
            query: (request) => ({
                method: "GET",
                url: `/submissions/${request.id}/submission-templates?templateKind=${request.templateKind}&campaignId=${request.campaignId}`
            }),
            transformResponse: (result) => result
        }),
        getCampaignSubmissions: builder.query({
            onQueryStarted: async (arg, thisApi) => {
                const { dispatch, queryFulfilled } = thisApi;
                try {
                    await queryFulfilled;
                    if (arg.campaignIds.length === 0) {
                        dispatch(setSelectedCampaign([]));
                    }
                }
                catch (error) {
                    dispatch(campaignErrorNotification());
                }
            },
            providesTags: ["Campaigns", "Submissions"],
            query: (request) => {
                const requestConfig = {
                    method: "GET",
                    url: `/submissions${request.campaignIds.length > 0 ? `?campaign_ids=${request.campaignIds.join("&campaign_ids=")}` : ""}`
                };
                return requestConfig;
            },
            transformResponse: (result) => result.submissions
        }),
        getCampaignSummary: builder.query({
            onQueryStarted: async (arg, thisApi) => {
                const { dispatch, queryFulfilled } = thisApi;
                try {
                    await queryFulfilled;
                }
                catch (error) {
                    dispatch(campaignErrorNotification());
                }
            },
            providesTags: ["Campaigns"],
            query: (request) => {
                const requestConfig = {
                    method: "GET",
                    url: `/aggregated-summaries${request.campaignIds.length > 0 ? `?campaign_ids=${request.campaignIds.join("&campaign_ids=")}` : ""}`
                };
                return requestConfig;
            }
        }),
        getCampaigns: builder.query({
            onQueryStarted: async (arg, thisApi) => {
                const { dispatch, queryFulfilled } = thisApi;
                try {
                    const { data } = await queryFulfilled;
                    if (data.campaigns.length > 0) {
                        dispatch(setSelectedCampaign([data.campaigns[0].id]));
                    }
                    else {
                        dispatch(setSelectedCampaign([]));
                    }
                }
                catch (error) {
                    dispatch(campaignErrorNotification());
                }
            },
            providesTags: ["Campaigns", "Submissions"],
            query: (request) => {
                const requestConfig = {
                    method: "GET",
                    url: `?reference_date=${request.asOfDate || getEndOfQuarter(new Date())}`
                };
                return requestConfig;
            },
            transformResponse: (result) => ({ campaigns: result.campaigns })
        }),
        getCampaignsLastAvailableDate: builder.query({
            onQueryStarted: async (arg, thisApi) => {
                const { dispatch, queryFulfilled } = thisApi;
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setQuarterSelected(data));
                }
                catch (error) {
                    dispatch(campaignErrorNotification());
                }
            },
            providesTags: ["Campaigns"],
            query: () => ({
                method: "GET",
                url: "/last-available-date"
            }),
            transformResponse: ({ lastAvailableDate }) => {
                if (!lastAvailableDate) {
                    return new Date();
                }
                const { day, month, year } = lastAvailableDate;
                if (!day || !month || !year) {
                    return new Date();
                }
                return new Date(`${year}-${month}-${day}`);
            }
        })
    }),
    reducerPath: `${moduleName}.api`,
    tagTypes: ["Campaigns", "Submissions"]
});
export const useCampaignsAPISelector = useSelector;
export const campaignsSelector = createSelector((asOfDate) => asOfDate || new Date().toDateString(), (asOfDate) => api.endpoints.getCampaigns.select({ asOfDate }));
export const { endpoints: { getCampaigns }, useLazyGetCampaignsQuery, useGetCampaignsQuery, useGetCampaignSummaryQuery, useGetCampaignSubmissionsQuery, useLazyGetCampaignSubmissionTemplateQuery, useGetCampaignsLastAvailableDateQuery, util, middleware: apiMiddleware, reducer: apiReducers, reducerPath: apiReducersPath } = api;
